import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Boxes } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";
import { API } from "@/lib/api";

const TIERS = [
  ["Bulk pricing", "Prices drop per box as you order more. Our standard bulk tiers apply automatically in the cart at checkout quantities."],
  ["Institutional orders", "For recurring or large-volume laboratory supply, send a quote request below and we'll confirm pricing, availability, and a shipping quote by email."],
  ["Confirmation", "Please allow up to 3 business days for confirmation. We reply from orders@lab2o.com with a total and payment details."],
];

const labelCls = "font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-500";

export default function Wholesale() {
  const [form, setForm] = useState({
    name: "", organization: "", email: "", phone: "",
    monthly_quantity: "", sizes_needed: "", notes: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/wholesale-quotes`, form);
      toast.success("Quote request received", { description: "We'll reply by email within 3 business days." });
      setForm({ name: "", organization: "", email: "", phone: "", monthly_quantity: "", sizes_needed: "", notes: "" });
    } catch {
      toast.error("Could not send request", { description: "Please email orders@lab2o.com directly." });
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell
      testid="wholesale-page"
      overline={<span className="flex items-center gap-2"><Boxes className="w-3.5 h-3.5" /> Bulk & institutional supply</span>}
      title="Wholesale and Bulk Bacteriostatic Water"
      intro="Volume pricing for laboratories and research facilities. Review the bulk tiers below, then request a quote for recurring or large orders."
    >
      <Seo
        title="Bacteriostatic Water Wholesale & Bulk | Lab2o"
        path="/wholesale"
        description="Bulk and wholesale bacteriostatic water for labs and resellers. Tiered case pricing, invoicing and consistent lot supply. Request a quote."
      />

      <div className="mt-12 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <h2 className="font-chivo font-extrabold tracking-tight text-2xl text-slate-950 mb-8">Bulk pricing tiers</h2>
          <ol className="flex flex-col">
            {TIERS.map(([title, body], i) => (
              <li key={title} className="flex gap-6 py-6 border-t border-slate-200" data-testid={`wholesale-point-${i + 1}`}>
                <span className="font-chivo font-black text-3xl text-outline leading-none pt-1 shrink-0 w-14">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-chivo font-bold text-lg text-slate-950">{title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="border border-slate-200 p-7 sm:p-10 self-start"
          data-testid="wholesale-form"
        >
          <h2 className="font-chivo font-extrabold tracking-tight text-2xl text-slate-950 mb-2">Request a quote</h2>
          <p className="text-sm text-slate-500 mb-8">Tell us about your lab's needs. Research reagents only.</p>

          <div className="flex flex-col gap-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="wq-name" className={labelCls}>Name *</Label>
                <Input id="wq-name" required value={form.name} onChange={set("name")} className="rounded-sm" data-testid="wholesale-name-input" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="wq-org" className={labelCls}>Organisation / Lab *</Label>
                <Input id="wq-org" required value={form.organization} onChange={set("organization")} className="rounded-sm" data-testid="wholesale-org-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="wq-email" className={labelCls}>Email *</Label>
                <Input id="wq-email" type="email" required value={form.email} onChange={set("email")} className="rounded-sm" data-testid="wholesale-email-input" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="wq-phone" className={labelCls}>Phone</Label>
                <Input id="wq-phone" value={form.phone} onChange={set("phone")} className="rounded-sm" data-testid="wholesale-phone-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="wq-qty" className={labelCls}>Estimated monthly quantity</Label>
                <Input id="wq-qty" value={form.monthly_quantity} onChange={set("monthly_quantity")} placeholder="e.g. 50 boxes" className="rounded-sm" data-testid="wholesale-qty-input" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="wq-sizes" className={labelCls}>Sizes needed</Label>
                <Input id="wq-sizes" value={form.sizes_needed} onChange={set("sizes_needed")} placeholder="e.g. 10 mL vials" className="rounded-sm" data-testid="wholesale-sizes-input" />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="wq-notes" className={labelCls}>Notes</Label>
              <Textarea id="wq-notes" rows={4} value={form.notes} onChange={set("notes")} className="rounded-sm resize-none" data-testid="wholesale-notes-input" />
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={sending}
              className="bg-slate-950 text-white py-4 font-plexmono text-xs tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
              data-testid="wholesale-submit-button"
            >
              <Send className="w-3.5 h-3.5" />
              {sending ? "Sending…" : "Request Quote"}
            </motion.button>
            <p className="text-red-700 text-xs font-medium leading-snug" data-testid="wholesale-disclaimer">
              For research purposes only. Not for human or veterinary use or consumption.
            </p>
          </div>
        </motion.form>
      </div>
    </PageShell>
  );
}
